import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  Resource_DetailProjection,
  ResourceModel
} from "@app/models/rest/resource";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import { Ticket, Ticket_DetailProjection } from "@app/models/rest/ticket";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { RouteService } from "@app/services/http/route.service";
import { RouteMessage } from "@app/models/dto/route-message";
import {
  ClientConfigService,
  TicketColoursConfig
} from "@app/core/config/client-config.service";

import Map from "ol/Map";
import View from "ol/View";
import Feature from "ol/Feature";
import { fromLonLat } from "ol/proj";
import Point from "ol/geom/Point";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import XYZ from "ol/source/XYZ";
import BingMaps from "ol/source/BingMaps";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import {
  Attribution,
  defaults as defaultControls,
  FullScreen,
  ScaleLine
} from "ol/control";
import {
  defaults as defaultInteractions,
  DragRotateAndZoom
} from "ol/interaction";
import { createEmpty, extend, Extent } from "ol/extent";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatCardModule } from "@angular/material/card";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from "@angular/material/core";
import { MatNativeDateModule } from "@angular/material/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MomentModule } from "ngx-moment";

// Class to hold route details for display
export class ResourceRouteDetail {
  public ticket: Ticket_DetailProjection;
  public route: RouteMessage;
  public background: string;
  public foreground: string;
}

@Component({
  selector: "scheduler-resource-route-map",
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatNativeDateModule,
    FormsModule,
    ReactiveFormsModule,
    MomentModule
  ],
  templateUrl: "./resource-route-map.component.html",
  styleUrl: "./resource-route-map.component.scss"
})
export class ResourceRouteMapComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  ticketStore = inject(TicketStreamingStore);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  dialog = inject(MatDialog);
  routeService = inject(RouteService);
  private clientConfigService = inject(ClientConfigService);

  @Input() resource: Resource_DetailProjection;
  @Input() dateFrom: Date = new Date();
  @Input() dateTo: Date = new Date();
  @Input() isDialog = false;
  @Output() ticketSelected = new EventEmitter<Ticket>();

  @ViewChild("mapElement") mapElement: ElementRef;

  // Navigation controls
  currentDate: Date = new Date();

  // State variables
  loading = true;
  pending = false;
  resourceRouteDetails: ResourceRouteDetail[] = [];
  selectedTicket: Ticket;

  // Map related properties
  private map: Map;
  private mainView: View;
  private selected: any;
  private routeLayer: VectorLayer<any>;
  private routeSource: VectorSource<any>;
  private jobLayer: VectorLayer<any>;
  private jobSource: VectorSource<any>;
  private highlightLayer: VectorLayer<any>;
  private highlightSource: VectorSource<any>;

  // Styling
  private styles = {
    route: [
      new Style({
        stroke: new Stroke({
          color: "rgba(0,42,255,0.5)",
          width: 6
        })
      }),
      new Style({
        stroke: new Stroke({
          color: "rgba(70,134,255,0.5)",
          width: 4
        })
      })
    ],
    job: new Style({
      image: new Icon({
        anchor: [0.5, 1],
        color: "rgb(78,14,119)",
        src: "assets/markers/job1.svg"
      })
    }),
    home: new Style({
      image: new Icon({
        anchor: [0.5, 1],
        color: "rgb(46,125,50)", // Green color for home
        src: "assets/markers/square.svg" // Different icon for home
      })
    })
  };

  private highlightStyles = {
    route: [
      new Style({
        stroke: new Stroke({
          color: "rgba(0,42,255,1)",
          width: 8
        })
      }),
      new Style({
        stroke: new Stroke({
          color: "rgba(70,134,255,1)",
          width: 6
        })
      })
    ],
    job: new Style({
      image: new Icon({
        anchor: [0.5, 1],
        color: "rgb(78,14,119)",
        src: "assets/markers/job1.svg"
      })
    }),
    home: new Style({
      image: new Icon({
        anchor: [0.5, 1],
        color: "rgb(46,125,50)", // Green color for home
        src: "assets/markers/square.svg" // Different icon for home
      })
    })
  };

  private loadDataPromise: Promise<any>;
  private _colors: TicketColoursConfig;

  constructor() {
    this._colors = this.clientConfigService.config.ticketColours;
    this.currentDate = new Date(this.dateFrom);
  }

  ngOnInit(): void {
    // Initialize date range if not provided
    if (!this.dateFrom) {
      this.dateFrom = new Date();
      this.dateFrom.setHours(0, 0, 0, 0);
    }

    if (!this.dateTo) {
      this.dateTo = new Date(this.dateFrom);
      this.dateTo.setHours(23, 59, 59, 999);
    }

    this.currentDate = new Date(this.dateFrom);

    // Subscribe to ticket updates
    this.ticketStore.updateStream?.subscribe((ticket: Ticket) => {
      if (!ticket) {
        return;
      }
      this.ticketStore
        .loadSingleProjection<Ticket_DetailProjection>(
          Ticket_DetailProjection,
          ticket.id,
          "detail"
        )
        .then((value) => {
          if (
            value.resourceAvailability &&
            value.resourceAvailability.resource.id === this.resource.id
          ) {
            this.resourceRouteDetails = [];
            if (this.loading) {
              this.pending = true;
            } else {
              this.loading = true;
              this.loadDataPromise = this.initData();
            }
          }
        });
    });
  }

  ngAfterViewInit(): void {
    this.loadDataPromise = this.initData();
    setTimeout(() => {
      this.initMap();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if resource or dates changed
    if (
      (changes.resource && !changes.resource.firstChange) ||
      (changes.dateFrom && !changes.dateFrom.firstChange) ||
      (changes.dateTo && !changes.dateTo.firstChange)
    ) {
      // Reset state
      this.resourceRouteDetails = [];
      this.loading = true;

      // Reload data and map
      if (this.map) {
        // Clear existing layers
        this.routeSource?.clear();
        this.jobSource?.clear();
        this.highlightSource?.clear();

        // Re-add home marker after clearing layers to ensure it's preserved
        this.addHomeLocationMarker();

        // Load new data
        this.loadDataPromise = this.initData();
      }
    }
  }

  // Navigation methods
  goToPreviousDay(): void {
    const prevDay = new Date(this.currentDate);
    prevDay.setDate(prevDay.getDate() - 1);
    this.navigateToDate(prevDay);
  }

  goToNextDay(): void {
    const nextDay = new Date(this.currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    this.navigateToDate(nextDay);
  }

  // Helper method to add home location marker
  private addHomeLocationMarker(): void {
    // Add home icon if resource has location - use direct styling approach
    if (
      this.resource &&
      this.resource.location &&
      this.resource.location.longitude &&
      this.resource.location.latitude
    ) {
      console.log("Adding home location marker for resource");

      // Create home icon and apply direct styling
      let homeIcon = new Feature({
        type: "home",
        geometry: new Point(
          fromLonLat([
            this.resource.location.longitude,
            this.resource.location.latitude
          ])
        )
      });

      // Apply custom styling directly to the feature for maximum visibility
      homeIcon.setStyle([
        new Style({
          image: new Icon({
            anchor: [0.5, 1],
            scale: 1.5, // Make it larger than normal icons
            color: "rgb(0,128,0)", // Bright green
            src: "assets/markers/square.svg"
          }),
          zIndex: 100 // Ensure it's on top
        }),
        new Style({
          text: new Text({
            text: "HOME",
            font: "bold 16px Calibri,sans-serif",
            offsetY: -35,
            fill: new Fill({
              color: "#000000"
            }),
            stroke: new Stroke({
              color: "#FFFFFF",
              width: 3
            })
          }),
          zIndex: 101
        })
      ]);

      this.jobSource.addFeature(homeIcon);
      console.log(
        "Home location marker added at:",
        this.resource.location.longitude,
        this.resource.location.latitude
      );
    } else {
      console.warn(
        "No home location found for resource:",
        this.resource ? this.resource.id : "undefined"
      );
    }
  }

  navigateToDate(date: Date): void {
    this.currentDate = new Date(date);

    // Set date range for the selected day
    this.dateFrom = new Date(date);
    this.dateFrom.setHours(0, 0, 0, 0);

    this.dateTo = new Date(date);
    this.dateTo.setHours(23, 59, 59, 999);

    // Reset and reload data
    this.resourceRouteDetails = [];
    this.loading = true;

    // Clear existing layers
    this.routeSource?.clear();
    this.jobSource?.clear();
    this.highlightSource?.clear();

    // Re-add home marker after clearing layers
    this.addHomeLocationMarker();

    // Load new data
    this.loadDataPromise = this.initData();
  }

  private loadAvailability(availability: ResourceAvailability): void {
    let viewExtent = createEmpty();
    let lastLocation = availability.resource.location;

    this.pending = false;
    let routePromises = [];
    let ticketSequence = 0;

    this.ticketStore
      .findByResourceAvailability(availability.id)
      .then((tickets) => {
        // Always turn off loading, even if no tickets are returned
        this.loading = false;

        if (!tickets.resources || tickets.resources.length === 0) {
          console.log("No tickets found for availability:", availability.id);
          return; // Exit early if no tickets
        }

        tickets.resources.forEach((ticket: Ticket_DetailProjection) => {
          ticketSequence++;
          let currentSequence = ticketSequence;
          let resourceRouteDetail = new ResourceRouteDetail();
          resourceRouteDetail.ticket = ticket;
          this.resourceRouteDetails.push(resourceRouteDetail);

          // Add marker
          let routePromise = this.routeService.getRoute(
            lastLocation,
            ticket.visit.job.location
          );

          routePromises.push(routePromise);
          routePromise.then((route: RouteMessage) => {
            resourceRouteDetail.route = route;

            let ticketIcon = new Feature({
              type: "job",
              geometry: new Point(
                fromLonLat([
                  ticket.visit.job.location.longitude,
                  ticket.visit.job.location.latitude
                ])
              ),
              ticket: ticket,
              ticketSequence: currentSequence
            });

            let segmentFeature = new Feature({
              type: "route",
              geometry: new GeoJSON().readGeometry(route.geometry, {
                featureProjection: "EPSG:3857",
                dataProjection: "EPSG:4326"
              })
            });

            this.jobSource.addFeature(ticketIcon);
            this.routeSource.addFeature(segmentFeature);

            extend(viewExtent, segmentFeature.getGeometry().getExtent());

            this.mainView.fit(viewExtent, {
              duration: 1000,
              padding: [50, 50, 50, 50]
            });
          });

          lastLocation = ticket.visit.job.location;
        });
      })
      .catch((error) => {
        console.error("Error loading tickets for availability:", error);
        this.loading = false; // Ensure loading is turned off even on error
      });
  }

  private postProcess(tempStyle, feature): any {
    if (feature.get("type") === "job") {
      tempStyle.setImage(
        new Icon({
          anchor: [0.5, 1],
          color: this._colors.status[feature.get("ticket").status]
            ? this._colors.status[feature.get("ticket").status].background
            : "#d1d1d1",
          src: "assets/markers/job1.svg"
        })
      );

      tempStyle.setText(
        new Text({
          // Default text settings
        })
      );

      return [
        tempStyle,
        new Style({
          text: new Text({
            text: feature.get("ticket").visit.job.externalJobReference,
            font: "Calibri,sans-serif",
            offsetY: -35,
            fill: new Fill({
              color: "#000000"
            })
          })
        }),
        new Style({
          text: new Text({
            text: "" + feature.get("ticketSequence"),
            font: "12px bold sans-serif",
            offsetY: -16,
            fill: new Fill({
              color: "#000000"
            })
          })
        })
      ];
    } else if (feature.get("type") === "home") {
      // Special styling for home location
      return [
        tempStyle,
        new Style({
          text: new Text({
            text: "HOME",
            font: "bold 14px Calibri,sans-serif",
            offsetY: -35,
            fill: new Fill({
              color: "#000000"
            }),
            stroke: new Stroke({
              color: "#FFFFFF",
              width: 2
            })
          })
        })
      ];
    }
    return tempStyle;
  }

  private mainStyleFunction(feature): any {
    let tempStyle = this.styles[feature.get("type")];
    return this.postProcess(tempStyle, feature);
  }

  private highlightStyleFunction(feature): any {
    let tempStyle = this.highlightStyles[feature.get("type")];
    return this.postProcess(tempStyle, feature);
  }

  private initData(): Promise<any> {
    if (!this.resource) {
      this.loading = false;
      return Promise.resolve();
    }

    return this.resourceAvailabilityStore
      .findByResourceAndTimeRange(
        this.resource.id,
        this.dateFrom,
        this.dateTo,
        true
      )
      .then((availabilities) => {
        console.log("Availabilities found:", availabilities.resources.length);

        // Turn off loading if no availabilities found
        if (
          !availabilities.resources ||
          availabilities.resources.length === 0
        ) {
          console.log("No availabilities found for resource");
          this.loading = false;
          return;
        }

        // Process each availability
        availabilities.resources.forEach(this.loadAvailability.bind(this));
      })
      .catch((error) => {
        console.error("Error loading availabilities:", error);
        this.loading = false; // Always turn off loading on error
      });
  }

  private initMap(): void {
    if (this.map) {
      this.map.dispose();
    }

    // Initialize map layers
    this.highlightSource = new VectorSource({
      features: []
    });

    this.highlightLayer = new VectorLayer({
      source: this.highlightSource,
      style: this.highlightStyleFunction.bind(this)
    });

    this.routeSource = new VectorSource({
      features: []
    });

    this.routeLayer = new VectorLayer({
      source: this.routeSource,
      style: this.mainStyleFunction.bind(this)
    });

    this.jobSource = new VectorSource({
      features: []
    });

    // Add home location marker using our helper method
    this.addHomeLocationMarker();

    this.jobLayer = new VectorLayer({
      source: this.jobSource,
      style: this.mainStyleFunction.bind(this)
    });

    // Set initial view - use resource location directly
    if (
      this.resource &&
      this.resource.location &&
      this.resource.location.longitude &&
      this.resource.location.latitude
    ) {
      this.mainView = new View({
        center: fromLonLat([
          this.resource.location.longitude,
          this.resource.location.latitude
        ]),
        zoom: 12
      });
      console.log("Setting map center to home location");
    } else {
      // Default center if no resource location
      console.warn(
        "No valid location for map center, using default coordinates"
      );
      this.mainView = new View({
        center: fromLonLat([-0.13947, 50.82]),
        zoom: 12
      });
    }

    // Map configuration
    const config = this.clientConfigService.config;
    const { bingKey, imagerySet, tileUrl } = config.maps;

    this.map = new Map({
      layers: [
        new TileLayer(
          bingKey && bingKey.length > 0
            ? ({
                visible: true,
                preload: Infinity,
                source: new BingMaps({
                  key: bingKey,
                  imagerySet: imagerySet,
                  maxZoom: 19
                })
              } as any)
            : ({
                source: new XYZ({
                  attributions: [
                    '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a> contributors'
                  ],
                  url: tileUrl
                })
              } as any)
        ),
        this.routeLayer,
        this.jobLayer,
        this.highlightLayer
      ],
      controls: defaultControls({
        attribution: true
      }).extend([
        new Attribution({
          collapsible: false
        }),
        new FullScreen(),
        new ScaleLine()
      ]),
      interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
      target: this.mapElement.nativeElement,
      view: this.mainView
    });

    // Event handlers
    this.map.on("pointermove", this.handlePointerMove.bind(this));
    this.map.on("click", this.handleMapClick.bind(this));

    // Force map to update its size after initialization
    setTimeout(() => {
      this.map.updateSize();
    }, 100);
  }

  private handleMapClick(e): void {
    this.highlightSource.clear();
    this.map.forEachFeatureAtPixel(
      e.pixel,
      this.selectFeaturesAtPixel.bind(this)
    );
  }

  private selectFeaturesAtPixel(f): void {
    this.highlightSource.addFeature(f);
    if (f.get("type") === "job") {
      const ticket = f.get("ticket");
      this.selectedTicket = ticket;
      this.ticketSelected.emit(ticket);
    }
  }

  private handlePointerMove(e): void {
    if (this.selected) {
      this.selected.setStyle(undefined);
      this.selected = null;
    }

    this.map.forEachFeatureAtPixel(
      e.pixel,
      this.hoverFeaturesAtPixel.bind(this)
    );
  }

  private hoverFeaturesAtPixel(f): boolean {
    if (!!f) {
      this.selected = f;
      switch (f.get("type")) {
        case "route":
          const highlightStyle = new Style({
            fill: new Fill({
              color: "rgba(255,255,255,0)"
            }),
            stroke: new Stroke({
              color: "rgb(0,0,0,1)",
              width: 8
            })
          });

          f.setStyle([
            highlightStyle,
            new Style({
              stroke: new Stroke({
                color: "rgba(0,42,255,1)",
                width: 6
              })
            }),
            new Style({
              stroke: new Stroke({
                color: "rgba(70,134,255,1)",
                width: 4
              })
            })
          ]);
          break;

        case "job":
          this.selectedTicket = f.get("ticket");
          break;

        default:
          break;
      }
    } else {
      this.selectedTicket = null;
      this.selected = null;
    }

    return true;
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.setTarget(null);
      this.map.dispose();
    }
  }
}
